/**
 * Intent: Staff marking where each signer must sign — a drafting overlay, not a form.
 * Palette: Deep Ocean / Sand plus per-signer colors from NDSign.
 * Depth: borders-only. Surfaces: same as the PDF canvas. Spacing: 8px.
 */

import React from 'react';
import { Box, Button, Chip, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import DrawIcon from '@mui/icons-material/Draw';
import {
  FIELD_TYPE_LABEL,
  FIELD_TYPES,
  signerColor,
  type FieldType,
  type OverlayField,
} from './fieldTypes';

interface SignerRow {
  key: string;
  name: string;
  email: string;
}

interface Props {
  signers: SignerRow[];
  fields: OverlayField[];
  activeSignerKey: string | null;
  armedType: FieldType | null;
  sending: boolean;
  onSelectSigner: (key: string) => void;
  onArmType: (type: FieldType | null) => void;
  onBack: () => void;
  onSkip: () => void;
  onSend: () => void;
}

const PrepareFieldsPanel: React.FC<Props> = ({
  signers,
  fields,
  activeSignerKey,
  armedType,
  sending,
  onSelectSigner,
  onArmType,
  onBack,
  onSkip,
  onSend,
}) => {
  const active = signers.find((s) => s.key === activeSignerKey) ?? null;
  const activeColor = signerColor(signers.findIndex((s) => s.key === activeSignerKey));
  const missing = signers.filter((s) => !fields.some((f) => f.signerId === s.key));

  return (
    <Box
      sx={{
        width: 272,
        flexShrink: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid',
        borderColor: 'divider',
        px: 1.5,
        py: 1.5,
        gap: 1.5,
        overflow: 'auto',
      }}
    >
      <Typography
        variant="overline"
        sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}
      >
        Signature spaces
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Select a signer, pick a type, then click the page. Drag to move, corner to resize.
      </Typography>

      <Stack spacing={0.75}>
        {signers.map((signer, index) => {
          const count = fields.filter((f) => f.signerId === signer.key).length;
          const selected = signer.key === activeSignerKey;
          const color = signerColor(index);
          return (
            <Box
              key={signer.key}
              onClick={() => onSelectSigner(signer.key)}
              sx={{
                cursor: 'pointer',
                borderRadius: 1,
                border: '1px solid',
                borderColor: selected ? color : 'divider',
                borderLeft: `4px solid ${color}`,
                bgcolor: selected ? `${color}18` : 'transparent',
                px: 1,
                py: 0.75,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Chip
                  size="small"
                  label={String(index + 1).padStart(2, '0')}
                  sx={{ fontFamily: 'monospace', height: 20, minWidth: 36 }}
                />
                <Typography variant="body2" noWrap sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                  {signer.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    color: count === 0 ? 'text.disabled' : color,
                    fontWeight: 700,
                  }}
                >
                  {count}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {signer.email}
              </Typography>
            </Box>
          );
        })}
      </Stack>

      <Divider />

      <Typography
        variant="overline"
        sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}
      >
        Place for {active?.name || 'signer'}
      </Typography>
      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
        {FIELD_TYPES.map((type) => {
          const armed = armedType === type;
          return (
            <Chip
              key={type}
              label={FIELD_TYPE_LABEL[type]}
              variant={armed ? 'filled' : 'outlined'}
              onClick={() => onArmType(armed ? null : type)}
              disabled={!activeSignerKey}
              sx={
                armed
                  ? { bgcolor: activeColor, color: '#0f2134', fontWeight: 700 }
                  : { borderColor: activeColor, color: activeColor }
              }
            />
          );
        })}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {fields.length === 0
          ? 'No spaces yet — send without fields to let signers place their own signature.'
          : missing.length > 0
            ? `Still need a space for ${missing.map((s) => s.name).join(', ')}.`
            : `${fields.length} space${fields.length === 1 ? '' : 's'} ready.`}
      </Typography>

      <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Button size="small" onClick={onBack} disabled={sending}>
          Back to signers
        </Button>
        <Button size="small" onClick={onSkip} disabled={sending}>
          Send without fields
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={onSend}
          disabled={sending}
          startIcon={sending ? <CircularProgress size={14} /> : <DrawIcon />}
          sx={{ bgcolor: '#ccba9b', color: '#0f2134', '&:hover': { bgcolor: '#b8a888' } }}
        >
          {sending ? 'Sending…' : 'Create envelope'}
        </Button>
      </Box>
    </Box>
  );
};

export default PrepareFieldsPanel;
